import { Routes } from '@angular/router';
import { withAuthShellGuard } from './auth-shell.guard';
// PBAC+ReBAC Phase 3: route data.entitlement seeds the not-entitled-in-place panel
// (PersonaGateOutletComponent). Codes mirror entitlement-codes.ts ROUTE_ENTITLEMENT, which is also
// the wrapper's URL-keyed fallback, so nav visibility and the in-place panel never diverge.
import { ENGINE_ACTION } from './entitlement-codes';
import { terminalPageGuard } from './terminal-page.guard';
import { customerSurfaceGuard } from './customer-surface.guard';
import { bankAuthorityGuard } from './bank-authority.guard';

/**
 * Lazy, standalone, per-feature routes (DESIGN.md -§2).
 * Route grouping mirrors the IA sections in app.component.ts nav.
 * A real auth/role guard can wrap these; the server stays the enforcer (-§11).
 */
export const routes: Routes = withAuthShellGuard([
  {
    path: 'customer-access-refused',
    loadComponent: () => import('./features/customer-access-refused.component').then(m => m.CustomerAccessRefusedComponent),
  },
  // Default landing: home/overview dashboard
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: 'signed-out',
    title: 'Signed out -- CashBro',
    // terminalPageGuard: authenticated users must never land on terminal/error routes.
    // If the session is active, redirect to /home. When not authenticated, allow normally.
    // This eliminates the navigation race on the debug host where the router's initial
    // navigation to /signed-out (browser URL after logout) can beat afterLogin()'s
    // navigateByUrl('/home') — the guard waits for the BFF session check to complete
    // and redirects within the navigation pipeline before the route component mounts.
    // Source of truth for which routes carry this guard: NON_RESUMABLE_URL_PREFIXES
    // in post-login-redirect.ts.
    canActivate: [terminalPageGuard],
    loadComponent: () => import('./features/signed-out.component').then((m) => m.SignedOutComponent),
  },
  // ADR-0051: persona-driven customizable home dashboard (replaces static HomeComponent).
  {
    path: 'home',
    title: 'Overview — CashBro',
    loadComponent: () =>
      import('./features/home-dashboard.component').then((m) => m.HomeDashboardComponent),
  },

  // ---------- ADR-0062 Favorites & Recents ----------
  {
    path: 'favorites',
    title: 'Favorites — CashBro',
    loadComponent: () => import('./features/favorites.component').then((m) => m.FavoritesPageComponent),
  },

  // ---------- Accounts & Cash ----------
  {
    path: 'accounts',
    title: 'Accounts — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_ACCOUNT },
    loadComponent: () => import('./features/accounts.component').then((m) => m.AccountsComponent),
  },
  {
    path: 'accounts/:id',
    title: 'Account — CashBro',
    loadComponent: () => import('./features/account-detail.component').then((m) => m.AccountDetailComponent),
  },
  {
    path: 'cash',
    title: 'Cash position — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_ACCOUNT },
    loadComponent: () => import('./features/cash.component').then((m) => m.CashComponent),
  },
  // ---------- ADR-0131 — Consolidated reporting (native-currency, non-statutory) ----------
  {
    path: 'consolidation-consents',
    title: 'Consolidation consent — CashBro',
    canActivate: [customerSurfaceGuard],
    loadComponent: () => import('./features/consolidation-consent/consolidation-consent.component')
      .then(m => m.ConsolidationConsentComponent),
  },
  {
    path: 'consolidated-reporting',
    title: 'Consolidated reporting — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_CONSOLIDATED },
    loadComponent: () =>
      import('./features/consolidated-reporting/consolidated-reporting.component').then(
        (m) => m.ConsolidatedReportingComponent,
      ),
  },

  // ---------- ADR-0142 — read-only legal-entity hierarchy (default-dark relay) ----------
  {
    path: 'legal-entities',
    title: 'Legal entities — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_ACCOUNT },
    loadComponent: () =>
      import('./features/legal-entity-hierarchy/legal-entity-hierarchy.component').then(
        (m) => m.LegalEntityHierarchyComponent,
      ),
  },

  // ---------- ADR-0060 Bills experience ----------
  {
    path: 'bills',
    title: 'Bills -- CashBro',
    loadComponent: () => import('./features/bills.component').then((m) => m.BillsComponent),
  },

  // ---------- Payments ----------
  {
    path: 'pay',
    title: 'Make a payment — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () => import('./features/pay.component').then((m) => m.PayComponent),
  },
  {
    path: 'payments/orders',
    title: 'Payment orders — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/payment-orders/payment-orders.component').then(
        (m) => m.PaymentOrdersComponent,
      ),
  },
  {
    path: 'payments/ach-collections',
    title: 'ACH debit collections — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/ach-collections/ach-collections.component').then(
        (m) => m.AchCollectionsComponent,
      ),
  },
  {
    path: 'payments/instant-payments',
    title: 'Instant payments — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () => import('./features/instant-payments/instant-payments.component').then(m => m.InstantPaymentsComponent),
  },
  {
    path: 'payments/gpi-tracker',
    title: 'Cross-border payment tracker — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/gpi-tracker/gpi-tracker.component').then(
        (m) => m.GpiTrackerComponent,
      ),
  },
  {
    path: 'payments/recalls',
    title: 'Payment recalls and returns — CashBro',
    loadComponent: () =>
      import('./features/recalls/recalls.component').then((m) => m.RecallsComponent),
  },
  {
    path: 'payments/order-approval',
    title: 'Payment order approvals — CashBro',
    data: { entitlement: ENGINE_ACTION.APPROVE_PAYMENT },
    loadComponent: () =>
      import('./features/order-approval/order-approval.component').then(
        (m) => m.OrderApprovalComponent,
      ),
  },
  {
    path: 'payments/managed-destinations',
    title: 'Managed destinations — CashBro',
    data: { entitlement: ENGINE_ACTION.MANAGE_BENEFICIARIES },
    loadComponent: () =>
      import('./features/managed-destinations/managed-destinations.component').then(
        (m) => m.ManagedDestinationsComponent,
      ),
  },
  {
    path: 'batch',
    title: 'Bulk payment — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () => import('./features/batch.component').then((m) => m.BatchComponent),
  },
  {
    path: 'payments/pending/:id',
    title: 'Pending payment -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.PaymentNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'payments/batches/:id',
    title: 'Payment batch -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.PaymentNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'iso20022-import',
    title: 'pain.001 import -- CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/iso20022-import.component').then((m) => m.Iso20022ImportComponent),
  },
  {
    path: 'scheduled',
    title: 'Scheduled payments — CashBro',
    data: { entitlement: ENGINE_ACTION.SCHEDULE_PAYMENT },
    loadComponent: () => import('./features/scheduled.component').then((m) => m.ScheduledComponent),
  },
  {
    path: 'templates',
    title: 'Payment templates — CashBro',
    data: { entitlement: ENGINE_ACTION.MANAGE_TEMPLATES },
    loadComponent: () => import('./features/templates.component').then((m) => m.TemplatesComponent),
  },
  {
    path: 'beneficiaries',
    title: 'Beneficiaries — CashBro',
    data: { entitlement: ENGINE_ACTION.MANAGE_BENEFICIARIES },
    loadComponent: () => import('./features/beneficiaries.component').then((m) => m.BeneficiariesComponent),
  },
  {
    path: 'approvals',
    title: 'Approvals — CashBro',
    data: { entitlement: ENGINE_ACTION.APPROVE_PAYMENT },
    loadComponent: () => import('./features/approvals.component').then((m) => m.ApprovalsComponent),
  },
  {
    path: 'payments/rfp',
    title: 'Request for Payment -- CashBro',
    loadComponent: () => import('./features/rfp-inbox.component').then((m) => m.RfpInboxComponent),
  },
  { path: 'rfp', pathMatch: 'full', redirectTo: 'payments/rfp' },
  {
    path: 'positive-pay',
    title: 'Positive pay exceptions -- CashBro',
    data: { entitlement: ENGINE_ACTION.PP_DECIDE_EXCEPTION },
    loadComponent: () =>
      import('./features/positive-pay.component').then((m) => m.PositivePayComponent),
  },
  {
    path: 'ach-debit-block',
    title: 'ACH debit block -- CashBro',
    data: { entitlement: ENGINE_ACTION.PP_CONFIGURE },
    loadComponent: () =>
      import('./features/ach-debit-block.component').then((m) => m.AchDebitBlockComponent),
  },
  {
    path: 'anomaly-alerts',
    title: 'Anomaly alerts -- CashBro',
    data: { entitlement: ENGINE_ACTION.PP_DECIDE_EXCEPTION },
    loadComponent: () =>
      import('./features/anomaly-alerts.component').then((m) => m.AnomalyAlertsComponent),
  },
  {
    path: 'positive-pay/issue-file',
    title: 'Upload issue file -- CashBro',
    loadComponent: () =>
      import('./features/positive-pay-issue-file.component').then(
        (m) => m.PositivePayIssueFileComponent,
      ),
  },
  // ADR-0057 — stop-payment orders (alongside the positive-pay exceptions surface)
  {
    path: 'positive-pay/stop-payments',
    title: 'Stop payments -- CashBro',
    loadComponent: () =>
      import('./features/stop-payments.component').then((m) => m.StopPaymentsComponent),
  },
  {
    path: 'fraud/anomalies/:id',
    title: 'Fraud alert -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.FraudNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'fraud/screening/:id',
    title: 'Payment screening -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.FraudNotificationActionDeepLinkComponent,
      ),
  },
  // ADR-0056 — controlled disbursement (configuration + treasurer morning funding view)
  {
    path: 'payments/disbursement',
    title: 'Controlled disbursement — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/disbursement.component').then((m) => m.DisbursementComponent),
  },
  { path: 'disbursement', pathMatch: 'full', redirectTo: 'payments/disbursement' },

  // ---------- Reporting / Balances (intraday + as-of rewind + format downloads) ----------
  {
    path: 'reporting',
    title: 'Reporting — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_ACCOUNT },
    loadComponent: () =>
      import('./features/balance-reporting.component').then((m) => m.BalanceReportingComponent),
  },

  // ---------- Activity & Reporting ----------
  {
    path: 'activity',
    title: 'Activity — CashBro',
    loadComponent: () => import('./features/activity.component').then((m) => m.ActivityComponent),
  },
  {
    path: 'activity-report',
    title: 'All activity — CashBro',
    loadComponent: () =>
      import('./features/activity-report.component').then((m) => m.ActivityReportComponent),
  },
  {
    path: 'reports',
    title: 'Reports — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_REPORTS },
    loadComponent: () => import('./features/reports.component').then((m) => m.ReportsComponent),
  },
  {
    path: 'reports/deliveries',
    title: 'Report deliveries -- CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_REPORTS },
    loadComponent: () =>
      import('./features/statement-schedules.component').then((m) => m.StatementSchedulesComponent),
  },
  {
    path: 'reports/distribution',
    title: 'Distribution plans -- CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_REPORTS },
    loadComponent: () =>
      import('./features/report-distribution/report-distribution.component').then(
        (m) => m.ReportDistributionComponent,
      ),
  },
  {
    path: 'financial-messages',
    title: 'Financial messages -- CashBro',
    data: { entitlement: ENGINE_ACTION.FINANCIAL_MESSAGE_VIEW },
    loadComponent: () => import('./features/financial-messages/financial-messages.component')
      .then((m) => m.FinancialMessagesComponent),
  },
  {
    path: 'file-transfers',
    title: 'Managed file transfers -- CashBro',
    data: { entitlement: ENGINE_ACTION.MFT_TRANSFER_VIEW },
    loadComponent: () => import('./features/mft/mft.component').then((m) => m.MftComponent),
  },
  {
    path: 'reports/history',
    title: 'Report history -- CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_REPORTS },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.OpsNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'reports/builder',
    title: 'Report builder — CashBro',
    loadComponent: () =>
      import('./features/report-builder.component').then((m) => m.ReportBuilderComponent),
  },
  {
    path: 'insights/ask',
    title: 'Ask insights -- CashBro',
    loadComponent: () =>
      import('./features/insights-ask.component').then((m) => m.InsightsAskComponent),
  },
  {
    path: 'insights',
    title: 'Insights — CashBro',
    loadComponent: () => import('./features/insights.component').then((m) => m.InsightsComponent),
  },

  // ---------- Products ----------
  {
    path: 'cards',
    title: 'Cards — CashBro',
    data: { entitlement: ENGINE_ACTION.CARD_ISSUE },
    loadComponent: () => import('./features/cards.component').then((m) => m.CardsComponent),
  },
  {
    path: 'cards/:id',
    title: 'Card -- CashBro',
    data: { entitlement: ENGINE_ACTION.CARD_ISSUE },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.CardNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'admin/cards/mcc-mappings',
    title: 'MCC category mappings -- CashBro',
    data: { entitlement: ENGINE_ACTION.CARD_SET_CONTROLS },
    loadComponent: () =>
      import('./features/mcc-mappings.component').then((m) => m.MccMappingsComponent),
  },
  {
    path: 'lending',
    title: 'Lending — CashBro',
    data: { entitlement: ENGINE_ACTION.FACILITY_APPLY },
    loadComponent: () => import('./features/lending.component').then((m) => m.LendingComponent),
  },
  {
    path: 'lending/facilities/:id',
    title: 'Credit facility -- CashBro',
    data: { entitlement: ENGINE_ACTION.FACILITY_APPLY },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.LendingNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'lending/drawdowns/:id',
    title: 'Credit drawdown -- CashBro',
    data: { entitlement: ENGINE_ACTION.FACILITY_APPLY },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.LendingNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'lending/covenants/:id',
    title: 'Credit covenant -- CashBro',
    data: { entitlement: ENGINE_ACTION.FACILITY_APPLY },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.LendingNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'deposits',
    title: 'Deposits — CashBro',
    data: { entitlement: ENGINE_ACTION.DEPOSIT_OPEN },
    loadComponent: () => import('./features/deposits.component').then((m) => m.DepositsComponent),
  },
  {
    path: 'deposits/:id',
    title: 'Deposit -- CashBro',
    data: { entitlement: ENGINE_ACTION.DEPOSIT_OPEN },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.DepositNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'merchant',
    title: 'Merchant acquiring — CashBro',
    data: { entitlement: ENGINE_ACTION.MERCHANT_BOARD },
    loadComponent: () => import('./features/merchant.component').then((m) => m.MerchantComponent),
  },

  // ---------- Integrations / ERP connections ----------
  {
    path: 'erp/connections',
    title: 'ERP connections -- CashBro',
    data: { entitlement: ENGINE_ACTION.API_CLIENT_MANAGE },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.ErpNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'erp/batches/:id',
    title: 'ERP AP batch -- CashBro',
    data: { entitlement: ENGINE_ACTION.API_CLIENT_MANAGE },
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.ErpNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'integrations',
    title: 'ERP connections — CashBro',
    data: { entitlement: ENGINE_ACTION.API_CLIENT_MANAGE },
    loadComponent: () => import('./features/integrations/integrations.component').then((m) => m.IntegrationsComponent),
  },
  // ADR-0049 — developer portal (API clients + webhooks + event catalog); FE-GAP close, fable lane.
  // Inherits the `integrations` → API_CLIENT_MANAGE gate by route-prefix (entitlement-codes.ts).
  {
    path: 'integrations/developer',
    title: 'Developer portal — CashBro',
    loadComponent: () =>
      import('./features/integrations/developer-portal.component').then((m) => m.DeveloperPortalComponent),
  },
  {
    path: 'integrations/:id/statement',
    title: 'Statement export — CashBro',
    loadComponent: () => import('./features/integrations/erp-statement.component').then((m) => m.ErpStatementComponent),
  },
  {
    path: 'integrations/:id/ap-batch',
    title: 'AP batch — CashBro',
    loadComponent: () => import('./features/integrations/erp-ap-batch.component').then((m) => m.ErpApBatchComponent),
  },
  {
    path: 'integrations/:id/ap-batch/:ref',
    title: 'Reconciliation — CashBro',
    loadComponent: () => import('./features/integrations/erp-reconcile.component').then((m) => m.ErpReconcileComponent),
  },

  // ---------- Onboarding journey ----------
  {
    path: 'onboarding',
    title: 'Open an account — CashBro',
    loadComponent: () => import('./features/onboarding-list.component').then((m) => m.OnboardingListComponent),
  },
  {
    path: 'onboarding/new',
    title: 'New application — CashBro',
    loadComponent: () => import('./features/onboarding-wizard.component').then((m) => m.OnboardingWizardComponent),
  },
  {
    path: 'onboarding/status/:applicationId',
    title: 'Application status — CashBro',
    loadComponent: () => import('./features/onboarding-status.component').then((m) => m.OnboardingStatusComponent),
  },
  {
    // Unified onboarding→origination journey (Fable track-2). Optional
    // ?originationApplicationId= binds via withComponentInputBinding.
    path: 'onboarding/journey/:onboardingApplicationId',
    title: 'Your application journey — CashBro',
    loadComponent: () => import('./features/application-journey.component').then((m) => m.ApplicationJourneyComponent),
  },

  // ---------- Credit origination journey ----------
  {
    path: 'financing',
    title: 'Financing — CashBro',
    loadComponent: () => import('./features/credit-origination-list.component').then((m) => m.CreditOriginationListComponent),
  },
  {
    path: 'financing/new',
    title: 'Apply for financing — CashBro',
    loadComponent: () => import('./features/credit-origination-wizard.component').then((m) => m.CreditOriginationWizardComponent),
  },
  {
    path: 'financing/status/:applicationId',
    title: 'Financing application — CashBro',
    loadComponent: () => import('./features/credit-origination-status.component').then((m) => m.CreditOriginationStatusComponent),
  },

  // ---------- Notifications ----------
  {
    path: 'notifications/agentic/:id',
    title: 'Notification analysis -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.OpsNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'notifications',
    title: 'Notifications — CashBro',
    loadComponent: () => import('./features/notifications.component').then((m) => m.NotificationsComponent),
  },
  {
    path: 'settings/notifications',
    title: 'Notification preferences — CashBro',
    loadComponent: () => import('./features/notification-preferences.component').then((m) => m.NotificationPreferencesComponent),
  },
  {
    path: 'settings/notifications/rules',
    title: 'Advanced notification rules — CashBro',
    loadComponent: () => import('./features/notification-rules.component').then((m) => m.NotificationRulesComponent),
  },
  {
    path: 'settings/display',
    title: 'Display & privacy — CashBro',
    loadComponent: () =>
      import('./features/display-preferences.component').then((m) => m.DisplayPreferencesComponent),
  },
  {
    path: 'settings/developer/webhooks',
    title: 'Webhook endpoints -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.OpsNotificationActionDeepLinkComponent,
      ),
  },

  // ---------- Security settings (Wave C — passkey enrollment) ----------
  {
    path: 'settings/privacy',
    title: 'Privacy & data rights — CashBro',
    loadComponent: () => import('./features/privacy-center.component').then((m) => m.PrivacyCenterComponent),
  },
  {
    path: 'settings/security',
    title: 'Security — CashBro',
    loadComponent: () => import('./features/security-settings.component').then((m) => m.SecuritySettingsComponent),
  },

  // ---------- Security hub + MFA lifecycle (security wave) ----------
  {
    path: 'security/factors',
    title: 'Security — CashBro',
    loadComponent: () => import('./features/security/security-hub.component').then((m) => m.SecurityHubComponent),
  },
  {
    path: 'security/factors/:id',
    title: 'Security factor -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.SecurityNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'security/backup-codes',
    title: 'Backup codes -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.SecurityNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'security/recovery',
    title: 'Account recovery -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.SecurityNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'security/hardware-token/order',
    title: 'Order hardware token — CashBro',
    loadComponent: () => import('./features/security/hardware-token-order.component').then((m) => m.HardwareTokenOrderComponent),
  },
  {
    path: 'security/orders/:id',
    title: 'Token order — CashBro',
    loadComponent: () => import('./features/security/hardware-token-tracking.component').then((m) => m.HardwareTokenTrackingComponent),
  },

  // ---------- Invitation sign-up (publicly accessible) ----------
  {
    path: 'sign-up/invitation/:token',
    title: 'Create your account — CashBro',
    loadComponent: () => import('./features/security/invitation-signup.component').then((m) => m.InvitationSignupComponent),
  },

  // ---------- KYC front door (publicly accessible through A3; notes/kyc-front-door/) ----------
  {
    path: 'open',
    loadChildren: () => import('./open/open.routes').then((m) => m.OPEN_ROUTES),
  },

  // ---------- ADR-0036 E1 — HRD interstitial (publicly accessible — pre-auth) ----------
  {
    path: 'login/hrd',
    title: 'Sign in — CashBro',
    loadComponent: () => import('./features/security/hrd-login.component').then((m) => m.HrdLoginComponent),
  },

  // ---------- ADR-0036 S7 — first-login factor enrollment (DEDICATED SCREEN, not a modal) ----------
  // The app shell redirects here (with ?returnUrl=) when a freshly-authenticated user has no
  // ACTIVE ToyBank step-up factor. The page clears the shared gate on success and navigates back.
  {
    path: 'login/enroll-factor',
    title: 'Set up verification — CashBro',
    loadComponent: () =>
      import('./features/security/first-login-enrollment.component').then(
        (m) => m.FirstLoginEnrollmentComponent,
      ),
  },

  // ---------- ADR-0031 Recovery flows (publicly accessible — no auth guard) ----------
  {
    path: 'recovery/login',
    title: 'Can\'t sign in? — CashBro',
    loadComponent: () => import('./features/security/recovery-login.component').then((m) => m.RecoveryLoginComponent),
  },
  {
    path: 'recovery/grant/:token',
    title: 'Account recovery — CashBro',
    loadComponent: () => import('./features/security/recovery-grant-landing.component').then((m) => m.RecoveryGrantLandingComponent),
  },

  // ---------- ADR-0031 Recovery enroll (requires active recovery session) ----------
  {
    path: 'recovery/enroll',
    title: 'Set up sign-in method — CashBro',
    loadComponent: () => import('./features/security/recovery-enroll.component').then((m) => m.RecoveryEnrollComponent),
  },

  // ---------- ADR-0037 E1' — agent authority management ----------
  // Principal-visible mandate list with one-tap revoke (honeypot mitigation) +
  // agent activity log. Requires an authenticated session.
  {
    path: 'agent-authority',
    title: 'Agent authority — CashBro',
    loadComponent: () =>
      import('./features/mandates/mandates.component').then((m) => m.MandatesPageComponent),
  },
  {
    path: 'entitlements/mandates/:id',
    title: 'Agent mandate -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.EntitlementMandateNotificationActionDeepLinkComponent,
      ),
  },
  {
    path: 'entitlements/decisions/:id',
    title: 'Authorization decision -- CashBro',
    loadComponent: () =>
      import('./notifications/notification-action-deep-link.component').then(
        (m) => m.EntitlementDecisionNotificationActionDeepLinkComponent,
      ),
  },

  // ---------- Customer-visible authority self-view (ADR-0114 demo visibility) ----------
  {
    path: 'entitlements',
    title: 'Your authority -- CashBro',
    loadComponent: () =>
      import('./features/authority-self-view/authority-self-view.component').then(
        (m) => m.AuthoritySelfViewComponent,
      ),
  },
  {
    path: 'authority',
    title: 'Authority graph -- CashBro',
    loadComponent: () =>
      import('./features/authority-graph-explorer.component').then(
        (m) => m.AuthorityGraphExplorerComponent,
      ),
  },


  // ADR-0039: assistant now lives in the shell so it can assist the current page.
  { path: 'assistant', pathMatch: 'full', redirectTo: 'home' },

  // -- issue #55 -- AI-provenance design-language showcase (browser verification + E2E) --
  {
    path: 'design/ai-provenance',
    title: 'AI-provenance design language -- CashBro',
    loadComponent: () =>
      import('./features/ai-provenance-showcase.component').then(
        (m) => m.AiProvenanceShowcaseComponent,
      ),
  },
  // ---------- Receivables / AR operations (#63 — exception queue + lockbox + RDC + recon rules) --
  {
    path: 'receivables',
    title: 'Receivables — CashBro',
    data: { entitlement: ENGINE_ACTION.RECON_RULE_MANAGE },
    loadComponent: () =>
      import('./features/receivables/receivables-hub.component').then(
        (m) => m.ReceivablesHubComponent,
      ),
    children: [
      // Default child: exceptions queue
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'exceptions',
      },
      {
        path: 'exceptions',
        title: 'Recon exceptions — CashBro',
        loadComponent: () =>
          import('./features/receivables/receivables-exception-queue.component').then(
            (m) => m.ReceivablesExceptionQueueComponent,
          ),
      },
      {
        path: 'lockbox',
        title: 'Lockbox — CashBro',
        loadComponent: () =>
          import('./features/receivables/lockbox-batches.component').then(
            (m) => m.LockboxBatchesComponent,
          ),
      },
      {
        path: 'rdc',
        title: 'RDC deposits — CashBro',
        loadComponent: () =>
          import('./features/receivables/rdc-deposits.component').then(
            (m) => m.RdcDepositsComponent,
          ),
      },
      {
        path: 'rules',
        title: 'Recon rules — CashBro',
        loadComponent: () =>
          import('./features/receivables/recon-rules.component').then(
            (m) => m.ReconRulesComponent,
          ),
      },
      {
        path: 'vam',
        title: 'Virtual accounts -- CashBro',
        loadComponent: () =>
          import('./features/receivables/vam-tree.component').then(
            (m) => m.VamTreeComponent,
          ),
      },
      // ADR-0053 — BaaS embedded-finance programs (FE-GAP close, fable lane).
      {
        path: 'baas',
        title: 'BaaS programs -- CashBro',
        loadComponent: () =>
          import('./features/receivables/baas-programs.component').then(
            (m) => m.BaasProgramsComponent,
          ),
      },
    ],
  },

  // ---------- ADR-0044 P1 — Help Center ----------
  {
    path: 'help',
    title: 'Help — CashBro',
    loadComponent: () =>
      import('./features/help/help-page.component').then((m) => m.HelpPageComponent),
  },
  // ---------- ADR-0029 — Live co-session help entry point (customer side) ----------
  {
    path: 'help/live-assist',
    title: 'Get live help — CashBro',
    loadComponent: () =>
      import('./cosession/cosession-live-help.component').then((m) => m.CosessionLiveHelpComponent),
  },
  {
    path: 'cosessions/:id/consent',
    title: 'Co-session request -- CashBro',
    loadComponent: () =>
      import('./cosession/cosession-consent-deep-link.component').then(
        (m) => m.CosessionConsentDeepLinkComponent,
      ),
  },


  // ---------- ADR-0046 #89 — Trade finance SPA ----------
  {
    path: 'trade',
    title: 'Trade finance — CashBro',
    data: { entitlement: ENGINE_ACTION.LC_OPEN },
    loadComponent: () => import('./features/trade.component').then((m) => m.TradeComponent),
  },

  // ---------- Treasury Services self-service adoption (Positive Pay instant path) ----------
  {
    path: 'treasury-services',
    title: 'Treasury services -- CashBro',
    data: { entitlement: ENGINE_ACTION.SWEEP_CONFIGURE },
    loadComponent: () =>
      import('./features/treasury-services/treasury-services.component').then(
        (m) => m.TreasuryServicesComponent,
      ),
  },
  {
    path: 'treasury-services/:id',
    title: 'Treasury service -- CashBro',
    loadComponent: () =>
      import('./features/treasury-services/treasury-service-detail.component').then(
        (m) => m.TreasuryServiceDetailComponent,
      ),
  },
  {
    path: 'treasury-services/:id/activate',
    title: 'Activate service -- CashBro',
    loadComponent: () =>
      import('./features/treasury-services/treasury-service-adoption.component').then(
        (m) => m.TreasuryServiceAdoptionComponent,
      ),
  },

  // ---------- ADR-0046 #91 — FX dealing + hedging ----------
  {
    path: 'customer-policy',
    title: 'Treasury-policy studio -- CashBro',
    // ADR-0097 W3 -- engine-gated (TREASURY_POLICY_MANAGE), matching the treasury-policy
    // service's own ActionKind (fable lane finding: this route shipped ungated on main).
    data: { entitlement: ENGINE_ACTION.TREASURY_POLICY_MANAGE },
    loadComponent: () =>
      import('./features/customer-policy-studio.component').then((m) => m.CustomerPolicyStudioComponent),
  },
  {
    path: 'implementations',
    title: 'Implementations workspace -- CashBro',
    // ADR-0096 -- engine-gated (IMPLEMENTATION_MANAGE), matching the implementations
    // service's own ActionKind (fable lane finding: this route shipped ungated on main).
    data: { entitlement: ENGINE_ACTION.IMPLEMENTATION_MANAGE },
    loadComponent: () =>
      import('./features/implementations-workspace.component').then((m) => m.ImplementationsWorkspaceComponent),
  },
  {
    path: 'fx',
    title: 'FX dealing — CashBro',
    data: { entitlement: ENGINE_ACTION.FX_DEAL_INITIATE },
    loadComponent: () => import('./features/fx.component').then((m) => m.FxComponent),
  },

  // ---------- ADR-0046 #92 — Liquidity console (sweeps + IHB + multibank) ----------
  {
    path: 'liquidity',
    title: 'Liquidity — CashBro',
    data: { entitlement: ENGINE_ACTION.SWEEP_EXECUTE },
    loadComponent: () => import('./features/liquidity.component').then((m) => m.LiquidityComponent),
  },

  // ---------- ADR-0046 #90 + #92 — gpi tracker + recall/return tools ----------
  {
    path: 'activity/payment-tools',
    title: 'Payment tools — CashBro',
    loadComponent: () =>
      import('./features/payment-tools.component').then((m) => m.PaymentToolsComponent),
  },

  // ---------- APP scam reimbursement claim lifecycle ----------
  {
    path: 'payments/app-claims',
    title: 'APP scam reimbursement — CashBro',
    data: { entitlement: ENGINE_ACTION.INITIATE_PAYMENT },
    loadComponent: () =>
      import('./features/app-claims-intake.component').then((m) => m.AppClaimsIntakeComponent),
  },
  { path: 'app-claims', pathMatch: 'full', redirectTo: 'payments/app-claims' },
  // ---------- ADR-0046 #92 — Account-analysis statement (under reports) ----------
  {
    path: 'reports/analysis-statement',
    title: 'Account analysis — CashBro',
    loadComponent: () =>
      import('./features/analysis-statement.component').then((m) => m.AnalysisStatementComponent),
  },

  {
    path: 'admin/users',
    canActivate: [customerSurfaceGuard],
    title: 'Users — CashBro',
    data: { entitlement: ENGINE_ACTION.USER_ADMIN },
    loadComponent: () => import('./features/organization-users/organization-users.component').then(m => m.OrganizationUsersComponent),
  },

  // ---------- ADR-0132 W2 — guided entitlement wizard + change-set review ----------
  // Flag toybank.entitlements.wizard.customer (front-end activation record); the route is always
  // registered (jml-bulk-console / Stress Lab convention) — a 503 from the backend flag
  // (toybank.entitlements.wizard.enabled) degrades to a clean "not enabled" notice.
  {
    path: 'admin/entitlements/wizard',
    title: 'Set up access — CashBro',
    loadComponent: () =>
      import('./features/entitlement-wizard/entitlement-wizard-page.component').then(
        (m) => m.EntitlementWizardPageComponent,
      ),
  },
  {
    path: 'admin/entitlements/change-sets',
    title: 'Entitlement change review — CashBro',
    loadComponent: () =>
      import('./features/entitlement-wizard/change-set-review-page.component').then(
        (m) => m.ChangeSetReviewPageComponent,
      ),
  },

  // ---------- ADR-0076 W1 entitlements landing hub ----------
  {
    path: 'admin/entitlements/home',
    title: 'Entitlements & Authority — CashBro',
    loadComponent: () =>
      import('./features/entitlements-home.component').then((m) => m.EntitlementsHomeComponent),
  },

  // ---------- ADR-0076 W2c prepared grant (org self-administration) ----------
  {
    path: 'admin/entitlements/grant',
    title: 'Grant access — CashBro',
    loadComponent: () =>
      import('./features/prepared-grant.component').then((m) => m.CashbroPreparedGrantComponent),
  },

  // ---------- ADR-0076 W3 checker — staged entitlement mutations review queue ----------
  {
    path: 'admin/entitlements/limits',
    canActivate: [customerSurfaceGuard],
    title: 'Limit changes -- CashBro',
    data: { entitlement: ENGINE_ACTION.LIMIT_ADMIN },
    loadComponent: () =>
      import('./features/limit-change.component').then((m) => m.LimitChangeComponent),
  },

  {
    path: 'bank/authority-increases',
    canActivate: [bankAuthorityGuard],
    title: 'Customer authority changes — CashBro',
    loadComponent: () =>
      import('./features/bank-authority-increase.component').then((m) => m.BankAuthorityIncreaseComponent),
  },

  {
    path: 'admin/entitlements/mutations',
    title: 'Approvals queue — CashBro',
    loadComponent: () =>
      import('./features/mutation-review.component').then((m) => m.MutationReviewPageComponent),
  },

  // ---------- ADR-0076 W5 customer self-service (Flow 1 self-view, Flow 2 request-access) ----------
  {
    path: 'admin/entitlements/my-authority',
    title: 'Your authority — CashBro',
    loadComponent: () =>
      import('./features/authority-self-view/authority-self-view.component').then(
        (m) => m.AuthoritySelfViewComponent,
      ),
  },
  {
    path: 'admin/entitlements/request-access',
    title: 'Request access — CashBro',
    loadComponent: () =>
      import('./features/authority-request-access/authority-request-access.component').then(
        (m) => m.AuthorityRequestAccessComponent,
      ),
  },

  // ---------- ADR-0067/0068 effective-permissions / who-can admin view ----------
  {
    path: 'admin/entitlements',
    title: 'Effective permissions — CashBro',
    data: { entitlement: ENGINE_ACTION.ENTITLEMENT_ADMIN },
    loadComponent: () =>
      import('./features/effective-permissions.component').then(
        (m) => m.EffectivePermissionsComponent,
      ),
  },

  // ---------- ADR-0071 advanced entitlement-graph surfaces (admin) ----------
  {
    path: 'admin/entitlements/cutover',
    title: 'Role template drift — CashBro',
    loadComponent: () =>
      import('./features/cutover-readiness.component').then((m) => m.CutoverReadinessComponent),
  },
  {
    path: 'admin/entitlements/approvers',
    title: 'Approver pool — CashBro',
    loadComponent: () =>
      import('./features/approver-pool.component').then((m) => m.ApproverPoolComponent),
  },
  {
    path: 'admin/entitlements/authority-graph',
    title: 'Authority graph — CashBro',
    loadComponent: () =>
      import('./features/authority-graph-explorer.component').then(
        (m) => m.AuthorityGraphExplorerComponent,
      ),
  },

  {
    path: 'admin/entitlements/rewind',
    title: 'Authority history — CashBro',
    loadComponent: () =>
      import('./features/authority-rewind/authority-rewind.component').then((m) => m.AuthorityRewindComponent),
  },
  // ADR-0076 I1 close-out — the org-wide authority change-log as a readable audit table (ADR-0072 G2 reads).
  {
    path: 'admin/entitlements/change-log',
    canActivate: [customerSurfaceGuard],
    title: 'Authority change-log — CashBro',
    loadComponent: () =>
      import('./features/authority-change-log.component').then((m) => m.AuthorityChangeLogComponent),
  },
  // ADR-0076 W7 / Batch-9 I4 — the security-admin step-up policy console (org-facing CRUD; floors read-only).
  {
    path: 'admin/entitlements/step-up-policies',
    title: 'Step-up policies — CashBro',
    loadComponent: () =>
      import('./features/step-up-policy-admin.component').then((m) => m.StepUpPolicyAdminComponent),
  },
  // ADR-0072 Phase 4 — reconciliation (did-vs-could LEAK / DORMANT exception queue).
  {
    path: 'admin/reconciliation',
    canActivate: [customerSurfaceGuard],
    title: 'Reconciliation — CashBro',
    loadComponent: () =>
      import('./features/reconciliation/reconciliation.component').then((m) => m.ReconciliationComponent),
  },

  // ---------- ADR-0078/0079 — shared Stress Lab shell (Policy + Cashflow modes) ----------
  // Flag-gated server-side (toybank.stresslab.* / toybank.entitlements.policy-lab.enabled,
  // all default-OFF -> 503); the page degrades to a "not enabled" notice (jml-console pattern).
  {
    path: 'admin/stress-lab',
    data: { entitlement: ENGINE_ACTION.REPORT_DEFINE },
    title: 'Stress Lab — CashBro',
    loadComponent: () =>
      import('./features/stress-lab/stress-lab.component').then((m) => m.StressLabComponent),
  },

  // ADR-0081 — Org admin self-view of RM Hub usage (OA audience, server-enforced)
  {
    path: 'admin/org-usage',
    canActivate: [customerSurfaceGuard],
    title: 'Usage summary — CashBro',
    loadComponent: () => import('./features/org-usage.component').then((m) => m.OrgUsageComponent),
  },
  
  // ---------- Billing fee operations (services/billing) ----------
  {
    path: 'admin/billing',
    title: 'Fee operations -- CashBro',
    data: { entitlement: ENGINE_ACTION.REPORT_DEFINE },
    loadComponent: () =>
      import('./features/billing-admin.component').then((m) => m.BillingAdminComponent),
  },

  // ---- ADR-0089 -- Recert reviewer inbox (manager) ----
  {
    path: 'admin/entitlements/recert-inbox',
    title: 'Access review inbox -- CashBro',
    loadComponent: () =>
      import('./features/recert-inbox/recert-inbox.component').then((m) => m.RecertInboxComponent),
  },

  // ---------- ADR-0143 — customer-company authenticator evidence (read-only) ----------
  {
    path: 'admin/authenticator-fleet',
    title: 'Authenticator fleet — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_AUTHENTICATOR_FLEET },
    loadComponent: () =>
      import('./features/authenticator-fleet/authenticator-fleet.component').then(
        (m) => m.AuthenticatorFleetComponent,
      ),
  },
  {
    path: 'admin/authenticator-fleet/recovery-routing',
    title: 'Assisted-recovery routing — CashBro',
    data: { entitlement: ENGINE_ACTION.VIEW_AUTHENTICATOR_FLEET },
    loadComponent: () =>
      import(
        './features/authenticator-fleet-recovery/authenticator-fleet-recovery.component'
      ).then((m) => m.AuthenticatorFleetRecoveryComponent),
  },

  // ---------- ADR-0093 — Org Security Center (session console + activity feed + payment freeze) ----------
  {
    path: 'admin/security-center',
    title: 'Security center — CashBro',
    loadComponent: () =>
      import('./features/security-center/security-center.component').then((m) => m.SecurityCenterComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'sessions' },
      {
        path: 'sessions',
        title: 'Sessions — CashBro',
        loadComponent: () =>
          import('./features/security-center/session-console.component').then((m) => m.SessionConsoleComponent),
      },
      {
        path: 'activity',
        title: 'Security activity — CashBro',
        loadComponent: () =>
          import('./features/security-center/security-activity.component').then((m) => m.SecurityActivityComponent),
      },
      {
        path: 'freeze',
        title: 'Payment freeze — CashBro',
        loadComponent: () =>
          import('./features/security-center/payment-freeze.component').then((m) => m.PaymentFreezeComponent),
      },
    ],
  },

  // ---- ADR-0082 -- Case centre (My Requests) ----
  {
    path: 'support/cases',
    title: 'My requests -- CashBro',
    loadComponent: () =>
      import('./features/case-centre/case-centre.component').then((m) => m.CaseCentreComponent),
  },
  {
    path: 'support/cases/new',
    title: 'Open a request -- CashBro',
    loadComponent: () =>
      import('./features/case-centre/case-open-wizard.component').then((m) => m.CaseOpenWizardComponent),
  },
  {
    path: 'support/cases/:id',
    title: 'Case detail -- CashBro',
    loadComponent: () =>
      import('./features/case-centre/case-detail.component').then((m) => m.CaseDetailComponent),
  },


  // ---- Lane 7c: subject-centric ReBAC+PBAC authority view ----
  {
    path: 'admin/entitlements/subject-authority',
    title: 'Subject-centric authority -- CashBro',
    loadComponent: () =>
      import('./features/subject-centric-authority.component').then(
        (m) => m.SubjectCentricAuthorityComponent,
      ),
  },

  // ---------- Self-service product hub (ADR-0019 scenario 1/2/3 entry surface) ----------
  {
    path: 'products',
    title: 'Products — CashBro',
    loadComponent: () =>
      import('./features/self-service-hub.component').then((m) => m.SelfServiceHubComponent),
  },
  {
    path: 'products/pre-approved',
    title: 'Pre-approved offers — CashBro',
    loadComponent: () =>
      import('./features/pre-approved-offers.component').then((m) => m.PreApprovedOffersComponent),
  },

  // ---------- XT-0912 #8: /backoffice is a DIFFERENT app (the Back-Office Console) ----------
  // it used to fall through the wildcard below with a silent redirect-to-home; a customer
  // typing/bookmarking it deserves to know it isn't a broken link in THIS app.
  {
    path: 'backoffice',
    title: 'Wrong portal — CashBro',
    children: [
      { path: '', loadComponent: () => import('./features/wrong-portal.component').then((m) => m.WrongPortalComponent) },
      { path: '**', loadComponent: () => import('./features/wrong-portal.component').then((m) => m.WrongPortalComponent) },
    ],
  },

  { path: '**', redirectTo: 'home' },
]);
